import { memo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  Drawer,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';

import FormNumberField from 'components/ui/form/FormNumberField';
import FormCurrencyField from 'components/ui/form/FormCurrencyField';
import FormSimpleSelectField from 'components/ui/form/FormSimpleSelectField';
import CurrencyFormat from 'components/ui/text-formats/CurrencyFormat';
import CalculatorForPrint from './CalculatorForPrint';
import { useReactToPrint } from 'react-to-print';
import useCurrency from 'hooks/useCurrency';

const validationSchema = yup.object({
  amount: yup.number().required('calculation.validation.amount'),
  percent: yup
    .number()
    .min(1, 'calculation.validation.min.percent')
    .max(100, 'calculation.validation.max.percent')
    .required('calculation.validation.percent'),
  months: yup
    .number()
    .min(6, 'calculation.validation.min.months')
    .max(360, 'calculation.validation.max.months')
    .required('calculation.validation.months'),
});

// Bitta portsiya (summa + stavka) uchun to'lov jadvali — o'zgarishsiz, sof funksiya
function calculateMortgage({ amount, percent, months, type }) {
  const monthlyRate = percent / 100 / 12;
  let schedule = [];
  let totalPayment = 0;
  let overpayment = 0;

  if (amount <= 0 || months <= 0) {
    return { schedule: [], totalPayment: '0.00', overpayment: '0.00' };
  }

  if (type === 'annuitet') {
    const annuityFactor =
      (monthlyRate * Math.pow(1 + monthlyRate, months)) /
      (Math.pow(1 + monthlyRate, months) - 1);
    const monthlyPayment = amount * annuityFactor;

    for (let i = 0; i < months; i++) {
      const interest = amount * monthlyRate;
      const principal = monthlyPayment - interest;
      amount -= principal;

      schedule.push({
        month: i + 1,
        payment: monthlyPayment,
        interest,
        principal,
        remaining: amount,
      });

      totalPayment += monthlyPayment;
    }
  }

  if (type === 'differensial') {
    const principalPart = amount / months;

    for (let i = 0; i < months; i++) {
      const interest = amount * monthlyRate;
      const monthlyPayment = principalPart + interest;
      amount -= principalPart;

      schedule.push({
        month: i + 1,
        payment: monthlyPayment,
        interest,
        principal: principalPart,
        remaining: amount,
      });

      totalPayment += monthlyPayment;
    }
  }

  overpayment =
    totalPayment - schedule.reduce((sum, item) => sum + item.principal, 0);

  return {
    schedule,
    totalPayment: totalPayment.toFixed(2),
    overpayment: overpayment.toFixed(2),
  };
}

// Ipoteka limiti + ikkilamchi kredit sxemasi: kiritilgan kredit summasi
// limitdan oshsa — limitgacha bo'lgan qism asosiy (subsidiya/MB) stavkada,
// limitdan oshgan qism ikkilamchi kredit stavkasida alohida hisoblanadi,
// so'ng ikkalasi bir oyga qo'shib (jami) ko'rsatiladi.
function buildCalculation(values, currencyData) {
  const creditNeeded = Math.max(Number(values.amount) || 0, 0);
  const months = Number(values.months);
  const type = values.type;
  const primaryRate = Number(values.percent) || 0;

  const limit = Number(currencyData?.mortgage_limit) || 0;
  const secondaryRateConfigured = Number(currencyData?.secondary_rate) || 0;

  const isSplit = limit > 0 && creditNeeded > limit;
  const portion1Amount = isSplit ? limit : creditNeeded;
  const portion2Amount = isSplit ? creditNeeded - limit : 0;
  const secondaryRateMissing = isSplit && secondaryRateConfigured <= 0;
  const portion2Rate = secondaryRateMissing
    ? primaryRate
    : secondaryRateConfigured;

  const schedule1 = calculateMortgage({
    amount: portion1Amount,
    percent: primaryRate,
    months,
    type,
  });
  const schedule2 =
    portion2Amount > 0
      ? calculateMortgage({
          amount: portion2Amount,
          percent: portion2Rate,
          months,
          type,
        })
      : null;

  const combinedSchedule = schedule1.schedule.map((row, i) => {
    const row2 = schedule2?.schedule?.[i];
    return {
      month: row.month,
      payment: row.payment + (row2?.payment || 0),
      principal: row.principal + (row2?.principal || 0),
      interest: row.interest + (row2?.interest || 0),
      remaining: row.remaining + (row2?.remaining || 0),
    };
  });

  const totalPayment =
    Number(schedule1.totalPayment) + Number(schedule2?.totalPayment || 0);
  const overpayment =
    Number(schedule1.overpayment) + Number(schedule2?.overpayment || 0);

  return {
    combined: {
      schedule: combinedSchedule,
      totalPayment: totalPayment.toFixed(2),
      overpayment: overpayment.toFixed(2),
    },
    split: {
      isSplit,
      creditNeeded,
      limit,
      secondaryRateMissing,
      portion1: {
        amount: portion1Amount,
        rate: primaryRate,
        monthly: schedule1.schedule[0]?.payment || 0,
        total: schedule1.totalPayment,
        overpayment: schedule1.overpayment,
      },
      portion2:
        portion2Amount > 0
          ? {
              amount: portion2Amount,
              rate: portion2Rate,
              monthly: schedule2.schedule[0]?.payment || 0,
              total: schedule2.totalPayment,
              overpayment: schedule2.overpayment,
            }
          : null,
    },
  };
}

const CalculatorDrawer = ({ open, setOpen }) => {
  const { t } = useTranslation();
  const [calculations, setCalculations] = useState({});
  const [splitInfo, setSplitInfo] = useState(null);
  const { currencyData } = useCurrency();
  const [isSubsidy, setIsSubsidy] = useState(false);
  // Subsidiyada mijoz stavkasi = MB asosiy stavkasining o'zi;
  // o'chirilganda foydalanuvchining avvalgi foizi qaytadi
  const bankRate = Number(currencyData?.bank_rate);
  const prevPercentRef = useRef('17.5');

  const printRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => printRef?.current,
    documentTitle: `calculation`,
    pageStyle: `
          @media print {
            @page { size: portrait; margin: 0mm; }
          }
          body {
            -webkit-print-color-adjust: exact;
          }
        `,
  });

  const formik = useFormik({
    initialValues: {
      amount: '',
      months: '240',
      percent: '17.5',
      type: 'annuitet',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const result = buildCalculation(values, currencyData);
      setCalculations(result.combined);
      setSplitInfo(result.split);
    },
  });

  const recalculateIfVisible = (nextValues) => {
    // natija ko'rinib turgan bo'lsa, yangi qiymatlar bilan darhol qayta hisoblaymiz
    if (nextValues.amount && calculations?.schedule?.length) {
      const result = buildCalculation(nextValues, currencyData);
      setCalculations(result.combined);
      setSplitInfo(result.split);
    }
  };

  const handleSubsidyChange = (e) => {
    const checked = e?.target?.checked;
    setIsSubsidy(checked);
    let newPercent = null;
    if (checked) {
      if (bankRate > 0) {
        prevPercentRef.current = formik.values.percent;
        newPercent = bankRate;
      }
    } else {
      newPercent = prevPercentRef.current;
    }
    if (newPercent !== null) {
      formik.setFieldValue('percent', newPercent);
      recalculateIfVisible({ ...formik.values, percent: newPercent });
    }
  };

  return (
    <Drawer anchor='right' open={open} onClose={() => setOpen(false)}>
      <div className='border-b flex items-center justify-between pr-16'>
        <div className='drawer-title p-2 pl-6 font-semibold text-lg'>
          <span className='mr-4'>{t('common.table.creditCalc')}</span>
        </div>
        <Button
          startIcon={<i className='bi bi-printer text-lg mr-1' />}
          variant='base'
          size='small'
          className='mr-4'
          onClick={handlePrint}
          disabled={!calculations?.overpayment}
        >
          {t('common.button.print')}
        </Button>
        <div className='close-btn-wrapper'>
          <IconButton
            variant='onlyIcon'
            color='primary'
            onClick={() => setOpen(false)}
          >
            <i className='bi bi-x' />
          </IconButton>
        </div>
      </div>
      <div className='calls-history-drawer-wrapper'>
        <div className='drawer-body hidden'>
          <div ref={printRef}>
            <CalculatorForPrint formik={formik} calculations={calculations} />
          </div>
        </div>
        <div className='drawer-body'>
          <form onSubmit={formik.handleSubmit}>
            <Grid
              container
              spacing={{ xs: 2, sm: 3, lg: 3 }}
              rowSpacing={1}
              columns={{ xs: 12, sm: 12, lg: 12 }}
            >
              <Grid item={true} lg={12} sm={12} xs={12}>
                <label className='flex items-center font-medium text-base'>
                  <Checkbox
                    checked={isSubsidy}
                    onChange={handleSubsidyChange}
                    size='small'
                  />
                  Subsidiya
                  {isSubsidy &&
                    (bankRate > 0 ? (
                      <span className='text-sm text-gray-500 ml-2'>
                        MB asosiy stavkasi: <b>{bankRate}%</b>
                      </span>
                    ) : (
                      <span className='text-sm text-orange-600 ml-2'>
                        MB stavkasi kiritilmagan — Sozlamalar → Valyuta kursi
                      </span>
                    ))}
                </label>
              </Grid>
              <Grid item={true} lg={6} sm={6} xs={12}>
                <FormSimpleSelectField
                  delay={0}
                  label={t('common.fields.creditCalculationType')}
                  fieldName='type'
                  options={[
                    { name: 'Annuitet (teng to‘lovli kredit)', id: 'annuitet' },
                    {
                      name: 'Differensial (kamayib boruvchi to‘lovli kredit)',
                      id: 'differensial',
                    },
                  ]}
                  formik={formik}
                />
              </Grid>
              <Grid item={true} lg={6} sm={6} xs={12}>
                <FormCurrencyField
                  delay={0.1}
                  label={t('calculation.creditSum')}
                  fieldName='amount'
                  formik={formik}
                />
              </Grid>
              <Grid item={true} lg={6} sm={6} xs={12}>
                <FormNumberField
                  delay={0.2}
                  label={t('common.fields.month')}
                  fieldName='months'
                  formik={formik}
                />
              </Grid>
              <Grid item={true} lg={6} sm={6} xs={12}>
                <FormCurrencyField
                  delay={0.3}
                  label={t('common.fields.percent')}
                  fieldName='percent'
                  formik={formik}
                />
              </Grid>

              <Grid item={true} lg={12} sm={12} xs={12}>
                <Button
                  type='submit'
                  fullWidth
                  variant='contained'
                  color='info'
                >
                  {t('common.button.calc')}
                </Button>
              </Grid>
            </Grid>
          </form>
        </div>

        {splitInfo?.isSplit && (
          <div className='mt-2 space-y-2'>
            {splitInfo.secondaryRateMissing && (
              <div className='p-2 rounded-lg bg-orange-50 text-orange-700 text-xs'>
                <i className='bi bi-exclamation-triangle mr-1' />
                {t('calculation.secondaryRateMissing')}
              </div>
            )}
            <div className='flex gap-2'>
              <div className='bg-blue-50 p-2 rounded-lg grow'>
                <div className='text-xs font-medium text-blue-700'>
                  {t('calculation.primaryCredit')}
                </div>
                <div className='text-sm mt-1'>
                  <CurrencyFormat value={splitInfo.portion1.amount} suffix='' />
                  <span className='text-gray-500'>
                    {' '}
                    · {t('calculation.rate')}: {splitInfo.portion1.rate}%
                  </span>
                </div>
                <div className='text-xs text-gray-500 mt-1'>
                  {t('shaxmatka.homeDetail.monthlyPay')}:{' '}
                  <CurrencyFormat
                    value={splitInfo.portion1.monthly}
                    decimalScale={0}
                    suffix=''
                  />
                </div>
              </div>
              <div className='bg-purple-50 p-2 rounded-lg grow'>
                <div className='text-xs font-medium text-purple-700'>
                  {t('calculation.secondaryCredit')}
                </div>
                <div className='text-sm mt-1'>
                  <CurrencyFormat value={splitInfo.portion2.amount} suffix='' />
                  <span className='text-gray-500'>
                    {' '}
                    · {t('calculation.rate')}: {splitInfo.portion2.rate}%
                  </span>
                </div>
                <div className='text-xs text-gray-500 mt-1'>
                  {t('shaxmatka.homeDetail.monthlyPay')}:{' '}
                  <CurrencyFormat
                    value={splitInfo.portion2.monthly}
                    decimalScale={0}
                    suffix=''
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className='mt-2 p-2 bg-gray-100 flex justify-between rounded-lg gap-2'>
          <div className='bg-gray-50 p-2 rounded-md grow'>
            <div>{t('payment.add.contract.totalSum')}</div>
            <span className='text-green-600'>
              <CurrencyFormat value={calculations?.totalPayment} suffix='' />
            </span>
          </div>
          <div className='bg-gray-50 p-2 rounded-md grow'>
            <div>{t('common.fields.overpayment')}</div>
            <span className='text-green-600'>
              <CurrencyFormat value={calculations?.overpayment} suffix='' />
            </span>
          </div>
          <div className='bg-gray-50 p-2 rounded-md grow'>
            <div>{t('calculation.totalMonthly')}</div>
            <span className='text-green-600'>
              <CurrencyFormat
                value={calculations?.schedule?.[0]?.payment}
                decimalScale={0}
                suffix=''
              />
            </span>
          </div>
        </div>
        <Accordion
          sx={{
            boxShadow: 'none',
          }}
          className={`dashboard-filter-accordion !my-2`}
        >
          <AccordionSummary
            aria-controls={`1-content`}
            id={`1-header`}
            data-text={t('shaxmatka.homeDetail.more')}
            sx={{ border: '1px solid' }}
            expandIcon={<i className='bi bi-chevron-right expand-icon' />}
            className='!border !border-gray-300 hover:!border-gray-500 !px-2 !shadow-none'
          >
            <i className='bi bi-table mr-2' />
            <span className='accordion-title'>
              {t('shaxmatka.homeDetail.more')}
            </span>
          </AccordionSummary>
          <AccordionDetails className='dashboard-filter-accordion-details !px-0'>
            <div className='calls-history-tab-item h-[calc(100vh-400px)]'>
              <TableContainer
                className='tab-table-container !shadow-none !base-border h-full'
                component='div'
              >
                <Table stickyHeader component='div'>
                  <TableHead component='div'>
                    <TableRow component='div'>
                      <TableCell component='div'>
                        {t('common.table.month')}
                      </TableCell>
                      <TableCell component='div'>
                        {t('shaxmatka.homeDetail.monthlyPay')}
                      </TableCell>
                      <TableCell component='div'>
                        {t('common.table.principal')}
                      </TableCell>
                      <TableCell component='div'>
                        {t('common.table.interest')}
                      </TableCell>
                      <TableCell component='div'>
                        {t('shaxmatka.moreHomeDetails.remainingPayment')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  {calculations?.schedule?.length > 0 ? (
                    <TableBody
                      className='overflow-hidden !bg-white'
                      component='div'
                    >
                      {calculations?.schedule?.map((row, rowIndex) => {
                        return (
                          <TableRow hover tabIndex={-1} key={'row-' + rowIndex}>
                            <TableCell size='small' component='div'>
                              {row?.month}
                            </TableCell>
                            <TableCell size='small' component='div'>
                              <CurrencyFormat
                                value={Number(row?.payment)}
                                decimalScale={2}
                                suffix=''
                              />
                            </TableCell>
                            <TableCell size='small' component='div'>
                              <CurrencyFormat
                                value={Number(row?.principal)}
                                decimalScale={2}
                                suffix=''
                              />
                            </TableCell>
                            <TableCell size='small' component='div'>
                              <CurrencyFormat
                                value={Number(row?.interest)}
                                decimalScale={2}
                                suffix=''
                              />
                            </TableCell>
                            <TableCell size='small' component='div'>
                              <CurrencyFormat
                                value={Number(row?.remaining)}
                                decimalScale={2}
                                suffix=''
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  ) : (
                    <TableBody component='div'>
                      <TableRow component='div'>
                        <TableCell colSpan={6}>
                          <span className='text-gray-400 flex items-center justify-center'>
                            <i className='bi bi-exclamation-octagon text-xl leading-4 mr-1' />{' '}
                            {t('common.global.noDataFound')}
                          </span>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  )}
                </Table>
              </TableContainer>
            </div>
          </AccordionDetails>
        </Accordion>
      </div>
    </Drawer>
  );
};
export default memo(CalculatorDrawer);
